import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import LocationOneAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/locations/location-one/location-one.entity";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  CombinedFilter,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  select,
  where,
  noop,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { ICreateLocationOne } from "./interfaces/create-location-one.interface";
import { IUpdateLocationOne } from "./interfaces/update-location-one.interface";
import { environment as env } from "../../../../../environments/environment.prod";
import { StatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums";
import { AbstractService } from "../../../../services/abstract.service";
import { RequestOptions } from "app/common/interfaces/options.interface";

const apiUrl = env.API_LOCATION_URL;
const moduleName = "location_one";

@Injectable({
  providedIn: "root",
})
export class LocationOneService extends AbstractService<
  LocationOneAttributes,
  ICreateLocationOne,
  IUpdateLocationOne
> {

  /** The last filter used to load the list */
  currentFilter: CombinedFilter<LocationOneAttributes>;

  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  findByFilter(filter: CombinedFilter<LocationOneAttributes>, options?: RequestOptions) {
    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(filter))}`;
      const headers: Record<string, any> = {};
      // see order-customer-export for more info about this header
      // ctx: stands for context
      if (options?.contextHeader) headers['x-ave-ctx'] = options.contextHeader;
      return this.http.get<IPaginationResult<LocationOneAttributes>>(API_URL, { headers });
  }
  
  /**
   * Pagination
   * @param options Pagination options
   * @param selectedStatuses selected statuses filter
   * @returns Paginated data
   */
  findAllWithCount(
    options: IPagination<LocationOneAttributes>,
    selectedStatuses: number[]
  ): Observable<IPaginationResult<LocationOneAttributes>> {
    const selectedStatusesIds = selectedStatuses && selectedStatuses?.length > 0 ? selectedStatuses : [];
    const shouldAddStatusFilter = selectedStatusesIds?.length > 0;
    
    const query = buildQuery<LocationOneAttributes>(
      where('_deleted', Ops.eq(false)),
      andWhere("code", Ops.like(options.search ?? "", "i")),
      orWhere("name", Ops.like(options.search ?? "", "i")),
      shouldAddStatusFilter ? andAllWhere('statusId', Ops.in(...selectedStatusesIds)) : noop() as any,
      orderBy(
        options.sort ?? "code",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<LocationOneAttributes>>(API_URL);
  }

  /**
   * Find to combo box
   * @returns Data
   */
  findToComboBox(): Observable<IRequestResult<LocationOneAttributes[]>> {
    const query = buildQuery<LocationOneAttributes>(
      where("statusId", StatusEnum.ACTIVE),
      select(["code", "name"]),
      orderBy("code", OrderVariant.Asc)
    );

    const API_URL: string =
      apiUrl +
      `api/${moduleName}/filter?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IRequestResult<LocationOneAttributes[]>>(API_URL);
  }
}
